// The person's own status (34-status.md): the chosen status in account data, sent as presence at once
// and with every sync; Online shown and sent as Idle after 10 minutes away; Do Not Disturb and quiet
// hours silencing this device's notifications.
import { ClientEvent, SetPresence, type MatrixClient, type MatrixEvent } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { formatTime } from '../core/format';
import { clock } from '../core/settings';
import { quietAt, type QuietHours } from '../push/quiet';

export type Status = 'online' | 'idle' | 'dnd' | 'invisible';
export const STATUSES: Status[] = ['online', 'idle', 'dnd', 'invisible'];
const STATUS_EVENT = 'chat.desyr.status';
export const QUIET_EVENT = 'chat.desyr.quiet_hours';
const AWAY_MS = 10 * 60_000;
/** Sent for Do Not Disturb in every language: other apps show it (decided). */
const DND_TEXT = 'Do Not Disturb';

let away = false;
/** A choice made here, until the account data brings it back. */
let chosenHere: Status | undefined;
const listeners = new Set<() => void>();
export const onStatus = (l: () => void) => (listeners.add(l), () => void listeners.delete(l));
const changed = () => listeners.forEach((l) => l());

/** The status in the account data; anything else counts as Online. */
export function chosenStatus(client: MatrixClient): Status {
  const s = chosenHere ?? client.getAccountData(STATUS_EVENT as never)?.getContent().status;
  return STATUSES.includes(s) ? s : 'online';
}

/** The status as others see it: Online turns Idle while away. */
export const shownStatus = (client: MatrixClient): Status => (chosenStatus(client) === 'online' && away ? 'idle' : chosenStatus(client));

export const quietHours = (client: MatrixClient): QuietHours => client.getAccountData(QUIET_EVENT as never)?.getContent<QuietHours>() ?? {};

/** This device's notifications and sounds stay silent: Do Not Disturb, or quiet hours now. */
export const silenced = (client: MatrixClient, now = new Date()) => chosenStatus(client) === 'dnd' || quietAt(now, quietHours(client));

/** The first minute that isn't quiet, while it is quiet now. */
export function quietUntil(q: QuietHours, now = new Date()): Date | undefined {
  if (!quietAt(now, q)) return undefined;
  const d = new Date(now);
  d.setSeconds(0, 0);
  for (let i = 0; i < 1440 && quietAt(d, q); i++) d.setMinutes(d.getMinutes() + 1);
  return d;
}

/** The end's time by the person's clock (without "All weekend" it is always today or tomorrow). */
export const untilText = (d: Date) => formatTime(d, clock());

const presenceFor = (s: Status) => (s === 'online' ? SetPresence.Online : s === 'invisible' ? SetPresence.Offline : SetPresence.Unavailable);

let sending = 0;
/** Sends the shown status now (rate limits waited out, at most three more tries, a newer one wins) and with every sync. */
async function send(client: MatrixClient) {
  const n = ++sending;
  const s = shownStatus(client);
  void client.setSyncPresence(presenceFor(s));
  for (let tries = 0; tries <= 3 && n === sending; tries++) {
    try {
      return await client.setPresence({ presence: presenceFor(s), status_msg: s === 'dnd' ? DND_TEXT : '' });
    } catch (e) {
      const wait = (e as { errcode?: string; data?: { retry_after_ms?: number } }).errcode === 'M_LIMIT_EXCEEDED' ? ((e as { data?: { retry_after_ms?: number } }).data?.retry_after_ms ?? 1000) : undefined;
      if (wait === undefined) return; // others see the old status for a while; nothing is shown
      await new Promise((r) => setTimeout(r, wait));
    }
  }
}

export function chooseStatus(client: MatrixClient, status: Status): void {
  chosenHere = status;
  changed();
  void send(client);
  void client.setAccountData(STATUS_EVENT as never, { status } as never).catch(() => {});
}

/** Keeps the status: the automatic idle, a choice from another device, the sync's presence. */
export function startStatus(client: MatrixClient): () => void {
  let last = Date.now();
  const back = () => {
    last = Date.now();
    if (!away) return;
    away = false;
    changed();
    if (chosenStatus(client) === 'online') void send(client);
  };
  const visible = () => document.visibilityState === 'visible' && back();
  const check = setInterval(() => {
    if (away || Date.now() - last < AWAY_MS) return;
    away = true;
    changed();
    if (chosenStatus(client) === 'online') void send(client);
  }, 30_000);
  const onData = (ev: MatrixEvent) => {
    if (ev.getType() === QUIET_EVENT) changed();
    if (ev.getType() !== STATUS_EVENT) return;
    const before = shownStatus(client);
    chosenHere = undefined;
    changed();
    if (shownStatus(client) !== before) void send(client);
  };
  const inputs = ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart', 'focus'];
  inputs.forEach((e) => addEventListener(e, back, { passive: true, capture: true }));
  document.addEventListener('visibilitychange', visible);
  client.on(ClientEvent.AccountData, onData);
  void client.setSyncPresence(presenceFor(shownStatus(client)));
  return () => {
    clearInterval(check);
    inputs.forEach((e) => removeEventListener(e, back, { capture: true }));
    document.removeEventListener('visibilitychange', visible);
    client.off(ClientEvent.AccountData, onData);
    away = false;
    chosenHere = undefined;
  };
}

/** Draws again when the status or quiet hours change, and every minute while quiet. */
export function useStatus(client: MatrixClient) {
  const [, redraw] = useState(0);
  useEffect(() => {
    const again = () => redraw((n) => n + 1);
    const off = onStatus(again);
    const tick = setInterval(again, 60_000);
    return () => (off(), clearInterval(tick));
  }, [client]);
  return { chosen: chosenStatus(client), shown: shownStatus(client), until: quietUntil(quietHours(client)) };
}
